import React from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import PainPointsSection from './components/PainPointsSection';
import SolutionsSection from './components/SolutionsSection';
import LogoTicker from './components/LogoTicker';
import CTASection from './components/CTASection';
import ClosingCTA from './components/ClosingCTA';
import Footer from './components/Footer';
import AboutPage from './components/AboutPage';
import ResourcesPage from './components/ResourcesPage';
import NewsletterArchivePage from './components/NewsletterArchivePage';
import NewsletterOctober2026Page from './components/NewsletterOctober2026Page';
import NewsletterAugust2026Page from './components/NewsletterAugust2026Page';
import NewsletterSubscribedPage from './components/NewsletterSubscribedPage';
import Glp1BridgeHubPage from './components/Glp1BridgeHubPage';
import MedicareGlp1BridgePage from './components/MedicareGlp1BridgePage';
import Glp1BridgeRecordingPage from './components/Glp1BridgeRecordingPage';
import MedicationCalculator from './components/MedicationCalculator';
import NewsPage from './components/NewsPage';
import M3PPage from './components/M3PPage';
import PostHogPageView from './components/PostHogPageView';
import SignupPage from './components/SignupPage';
import Glp1BridgeAssistantPage from './pages/GLP1BridgeAssistant/Glp1BridgeAssistantPage';

const LegalPage = React.lazy(() => import('./components/LegalPage'));

class LegalPageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <main className="mx-auto min-h-[50vh] max-w-3xl px-6 py-24 text-left">
          <h1 className="text-3xl font-semibold text-[#173b5f]">This page did not load</h1>
          <p className="mt-4 text-[#424245]">
            Please reload the page to get the latest version of this legal document.
          </p>
          <button
            type="button"
            className="mt-6 min-h-11 rounded-full bg-[#14778a] px-5 font-medium text-white"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </main>
      );
    }

    return this.props.children;
  }
}

function LegalPageRoute({ documentKey }) {
  return (
    <LegalPageErrorBoundary>
      <React.Suspense
        fallback={(
          <main aria-busy="true" className="min-h-[50vh]">
            <span className="sr-only">Loading legal document…</span>
          </main>
        )}
      >
        <LegalPage documentKey={documentKey} />
      </React.Suspense>
    </LegalPageErrorBoundary>
  );
}

function HomePage() {
  return (
    <>
      <HeroSection />
      <PainPointsSection />
      <SolutionsSection />
      <LogoTicker />
      <CTASection />
      <ClosingCTA />
    </>
  );
}

function AppContent() {
  const location = useLocation();
  const isAssistantRoute = location.pathname === '/resources/glp1-bridge/assistant';

  return (
    <>
      <PostHogPageView />
      <div className="App">
        <Header />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route path="/resources/newsletter" element={<NewsletterArchivePage />} />
          <Route path="/resources/newsletter/1026" element={<NewsletterOctober2026Page />} />
          <Route path="/resources/newsletter/0826" element={<NewsletterAugust2026Page />} />
          <Route path="/resources/newsletter/subscribed" element={<NewsletterSubscribedPage />} />
          <Route path="/resources/glp1-bridge" element={<Glp1BridgeHubPage />} />
          <Route path="/resources/glp1-bridge/guide" element={<MedicareGlp1BridgePage />} />
          <Route path="/resources/glp1-bridge/recording" element={<Glp1BridgeRecordingPage />} />
          <Route path="/resources/glp1-bridge/assistant" element={<Glp1BridgeAssistantPage />} />
          <Route path="/glp1-bridge" element={<Navigate to="/resources/glp1-bridge" replace />} />
          <Route path="/medication-calculator" element={<Navigate to="/m3p" />} />
          <Route path="/m3p" element={<MedicationCalculator />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/resources/m3p" element={<M3PPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/privacy-policy" element={<LegalPageRoute documentKey="privacy" />} />
          <Route path="/terms-of-use" element={<LegalPageRoute documentKey="terms" />} />
        </Routes>
        {isAssistantRoute ? null : <Footer />}
      </div>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
